import type { RectangleCorner, RectangleEdge } from './markupGeometry'
import { MIN_ANNOTATION_SIZE, rectangleFromPoints } from './markupGeometry'
import type { MarkupRectangle } from './types'

type HandleAxis = 'minimum' | 'maximum' | 'center'
type ResizeHandle = readonly [
  string,
  'edge' | 'corner',
  RectangleCorner | RectangleEdge,
  HandleAxis,
  HandleAxis,
]

export const rectangleResizeHandles = [
  ['top', 'edge', 'top', 'center', 'minimum'],
  ['right', 'edge', 'right', 'maximum', 'center'],
  ['bottom', 'edge', 'bottom', 'center', 'maximum'],
  ['left', 'edge', 'left', 'minimum', 'center'],
  ['topLeft', 'corner', 'top-left', 'minimum', 'minimum'],
  ['topRight', 'corner', 'top-right', 'maximum', 'minimum'],
  ['bottomLeft', 'corner', 'bottom-left', 'minimum', 'maximum'],
  ['bottomRight', 'corner', 'bottom-right', 'maximum', 'maximum'],
] as const satisfies ReadonlyArray<ResizeHandle>

export const rectangleHandleDescription = (
  target: 'edge' | 'corner',
  value: RectangleCorner | RectangleEdge
) => (target === 'corner' ? `the ${value} corner` : `${value} edge`)

const resizedAxis = (
  axis: HandleAxis,
  min: number,
  max: number,
  delta: number
): [number, number] =>
  axis === 'minimum'
    ? [min + delta, max]
    : axis === 'maximum'
      ? [min, max + delta]
      : [min, max]

export const resizeRectangleFromHandle = (
  rectangle: MarkupRectangle,
  value: string,
  deltaX: number,
  deltaY: number,
  minimumSize?: { width: number; height: number },
  allowOutsidePage = false
) => {
  const handle = rectangleResizeHandles.find(
    ([, , candidate]) => candidate === value
  )
  if (!handle) return null
  const [xMin, xMax] = resizedAxis(
    handle[3],
    rectangle.x_min,
    rectangle.x_max,
    deltaX
  )
  const [yMin, yMax] = resizedAxis(
    handle[4],
    rectangle.y_min,
    rectangle.y_max,
    deltaY
  )
  if (!allowOutsidePage && !minimumSize)
    return rectangleFromPoints({ x: xMin, y: yMin }, { x: xMax, y: yMax })
  const minimumWidth = minimumSize?.width ?? MIN_ANNOTATION_SIZE
  const minimumHeight = minimumSize?.height ?? MIN_ANNOTATION_SIZE
  const fittedXMin =
    xMax - xMin < minimumWidth
      ? handle[3] === 'minimum'
        ? xMax - minimumWidth
        : xMin
      : xMin
  const fittedXMax =
    xMax - xMin < minimumWidth
      ? handle[3] === 'maximum'
        ? xMin + minimumWidth
        : xMax
      : xMax
  const fittedYMin =
    yMax - yMin < minimumHeight
      ? handle[4] === 'minimum'
        ? yMax - minimumHeight
        : yMin
      : yMin
  const fittedYMax =
    yMax - yMin < minimumHeight
      ? handle[4] === 'maximum'
        ? yMin + minimumHeight
        : yMax
      : yMax
  return rectangleFromPoints(
    { x: fittedXMin, y: fittedYMin },
    { x: fittedXMax, y: fittedYMax },
    { width: 0, height: 0 },
    { allowOutsidePage }
  )
}
